import * as React from "react";
import Club from "../api/models/Club";

interface ClubsContextData {
  activeGroupId: string | null;
  selectedClubs: Club[];
  setActiveGroup: (groupId?: string) => void;
  addClub: (club: Club) => void;
  removeClub: (clubId: string) => void;
  clearClubs: () => void;
}

const initialState: ClubsContextData = {
  activeGroupId: null,
  selectedClubs: [],
  setActiveGroup: () => {},
  addClub: () => {},
  removeClub: () => {},
  clearClubs: () => {},
};

const ClubsContext = React.createContext<ClubsContextData>(initialState);

export function ClubsProvider({ children }: { children: React.ReactNode }) {
  const [activeGroupId, setActiveGroupId] = React.useState<string | null>(null);
  const [selectedClubs, setSelectedClubs] = React.useState<Club[]>([]);

  const setActiveGroup = React.useCallback((groupId?: string) => {
    const nextGroupId = groupId ?? null;

    setActiveGroupId((previousGroupId) => {
      if (previousGroupId !== nextGroupId) {
        setSelectedClubs([]);
      }

      return nextGroupId;
    });
  }, []);

  const addClub = React.useCallback((club: Club) => {
    setSelectedClubs((previousClubs) =>
      previousClubs.some((selectedClub) => selectedClub.id === club.id)
        ? previousClubs
        : [...previousClubs, club]
    );
  }, []);

  const removeClub = React.useCallback((clubId: string) => {
    setSelectedClubs((previousClubs) =>
      previousClubs.filter((club) => club.id !== clubId)
    );
  }, []);

  const clearClubs = React.useCallback(() => {
    setSelectedClubs([]);
  }, []);

  const value = React.useMemo(
    () => ({
      activeGroupId,
      selectedClubs,
      setActiveGroup,
      addClub,
      removeClub,
      clearClubs,
    }),
    [activeGroupId, selectedClubs, setActiveGroup, addClub, removeClub, clearClubs]
  );

  return (
    <ClubsContext.Provider value={value}>
      {children}
    </ClubsContext.Provider>
  );
}

export function useClubs() {
  const context = React.useContext(ClubsContext);

  return context;
}
